import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Settings,
  DollarSign,
  TrendingUp,
  Users,
  FolderKanban,
  Mail,
  Server,
  Eye,
  EyeOff,
  Save,
  Loader2,
  CheckCircle2,
  HardDrive,
  Cloud,
  ExternalLink,
  Lock,
  Pencil,
  Briefcase,
  Hammer,
  Building2,
  Activity,
  Key,
  Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import TenantSecureLoginsFolder from '../components/TenantSecureLoginsFolder';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businessTypes = [
  { value: 'web_marketing', label: 'Web & Marketing Agency', icon: Briefcase, description: 'Web design, SEO, digital marketing services' },
  { value: 'construction', label: 'Construction Contractor', icon: Hammer, description: 'Construction, renovation, contracting services' },
  { value: 'dme_medical', label: 'DME Medical', icon: Activity, description: 'Durable Medical Equipment billing and order management' }
];

const TenantSettings = () => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showStorageKey, setShowStorageKey] = useState(false);
  
  // SMTP Settings
  const [useCustomSmtp, setUseCustomSmtp] = useState(false);
  const [smtpLocked, setSmtpLocked] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    is_active: false
  });
  
  // Storage Settings
  const [storageSettings, setStorageSettings] = useState({
    endpoint: '',
    region: '',
    bucket_name: '',
    access_key_id: '',
    secret_access_key: '',
    is_active: false
  });

  // Business Profile
  const [businessProfile, setBusinessProfile] = useState({
    company_name: '',
    contact_email: '',
    phone: '',
    address: '',
    business_type: 'web_marketing'
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, smtpRes, storageRes, tenantRes] = await Promise.all([
        axios.get(`${API_URL}/tenant-settings/stats`, getAuthHeaders()),
        axios.get(`${API_URL}/tenant-settings/smtp`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/tenant-settings/storage`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/tenants/${user.organization_id}`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      
      if (smtpRes.data) {
        setSmtpSettings({ ...smtpSettings, ...smtpRes.data, password: '' });
        // If SMTP settings exist and are active, set custom SMTP as enabled and locked
        if (smtpRes.data.is_active && smtpRes.data.host) {
          setUseCustomSmtp(true);
          setSmtpLocked(true);
        }
      }
      if (storageRes.data) {
        setStorageSettings({ ...storageSettings, ...storageRes.data, secret_access_key: '' });
      }
      if (tenantRes.data) {
        setBusinessProfile({
          company_name: tenantRes.data.company_name || '',
          contact_email: tenantRes.data.contact_email || '',
          phone: tenantRes.data.phone || '',
          address: tenantRes.data.address || '',
          business_type: tenantRes.data.business_type || 'web_marketing'
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/tenants/${user.organization_id}`, businessProfile, getAuthHeaders());
      toast.success('Business profile saved');
    } catch (error) {
      toast.error('Failed to save business profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/tenant-settings/smtp`, smtpSettings, getAuthHeaders());
      toast.success('SMTP settings saved');
      setSmtpLocked(true); // Lock after successful save
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableCustomSmtp = async () => {
    setSaving(true);
    try {
      // Disable custom SMTP by setting is_active to false
      await axios.post(`${API_URL}/tenant-settings/smtp`, { ...smtpSettings, is_active: false }, getAuthHeaders());
      setUseCustomSmtp(false);
      setSmtpLocked(false);
      setSmtpSettings({
        host: '',
        port: 587,
        username: '',
        password: '',
        from_email: '',
        from_name: '',
        use_tls: true,
        is_active: false
      });
      toast.success('Switched to platform email. Your custom SMTP has been disabled.');
    } catch (error) {
      toast.error('Failed to disable custom SMTP');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStorage = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/tenant-settings/storage`, storageSettings, getAuthHeaders());
      toast.success('Storage settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save storage settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="tenant-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          <Settings className="inline mr-3 text-slate-600" size={32} />
          Business Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Manage your business revenue, email, and storage settings
        </p>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="p-3 rounded-lg bg-emerald-50">
                <DollarSign size={24} className="text-emerald-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-900">
                ${(stats?.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-slate-500">Total Revenue</p>
              <p className="text-xs text-slate-400 mt-1">From your clients</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="p-3 rounded-lg bg-blue-50">
                <TrendingUp size={24} className="text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-900">
                ${(stats?.pending_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-slate-500">Pending Revenue</p>
              <p className="text-xs text-slate-400 mt-1">Awaiting payment</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="p-3 rounded-lg bg-purple-50">
                <Users size={24} className="text-purple-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-900">{stats?.total_clients || 0}</p>
              <p className="text-sm text-slate-500">Total Clients</p>
              <p className="text-xs text-slate-400 mt-1">Active client accounts</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="p-3 rounded-lg bg-amber-50">
                <FolderKanban size={24} className="text-amber-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-slate-900">{stats?.total_projects || 0}</p>
              <p className="text-sm text-slate-500">Total Projects</p>
              <p className="text-xs text-slate-400 mt-1">{stats?.active_projects || 0} active</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings Tabs */}
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="profile" className="data-[state=active]:bg-white">
            <Building2 size={16} className="mr-2" />
            Business Profile
          </TabsTrigger>
          <TabsTrigger value="vault" className="data-[state=active]:bg-white">
            <Key size={16} className="mr-2" />
            Secure Vault
          </TabsTrigger>
          <TabsTrigger value="smtp" className="data-[state=active]:bg-white">
            <Mail size={16} className="mr-2" />
            Email (SMTP)
          </TabsTrigger>
          <TabsTrigger value="storage" className="data-[state=active]:bg-white">
            <Cloud size={16} className="mr-2" />
            Cloud Storage
          </TabsTrigger>
        </TabsList>

        {/* Business Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                <Building2 size={20} className="text-slate-600" />
                Business Profile
              </CardTitle>
              <CardDescription>Update your business information and type</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Business Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Business Type</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {businessTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = businessProfile.business_type === type.value;
                    const colors = {
                      construction: { border: 'border-amber-500 bg-amber-50', icon: 'bg-amber-500 text-white', check: 'text-amber-500' },
                      dme_medical:  { border: 'border-red-500 bg-red-50',    icon: 'bg-red-500 text-white',    check: 'text-red-500' },
                      web_marketing:{ border: 'border-blue-500 bg-blue-50',  icon: 'bg-blue-500 text-white',   check: 'text-blue-500' },
                    };
                    const c = colors[type.value] || colors.web_marketing;
                    return (
                      <div
                        key={type.value}
                        onClick={() => setBusinessProfile({ ...businessProfile, business_type: type.value })}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected ? c.border : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? c.icon : 'bg-slate-100 text-slate-500'}`}>
                            <Icon size={20} />
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${isSelected ? 'text-slate-900' : 'text-slate-700'}`}>{type.label}</p>
                            <p className="text-sm text-slate-500 mt-0.5">{type.description}</p>
                          </div>
                          {isSelected && <CheckCircle2 size={20} className={c.check} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  This determines which project forms and quote templates are available to your team.
                </p>
              </div>

              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-sm font-medium text-slate-900 mb-4">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={businessProfile.company_name}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, company_name: e.target.value })}
                      placeholder="Your Company Name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={businessProfile.contact_email}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, contact_email: e.target.value })}
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={businessProfile.phone}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={businessProfile.address}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, address: e.target.value })}
                      placeholder="123 Main St, City, State"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-100">
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-slate-900 hover:bg-slate-800">
                  {savingProfile ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Save size={16} className="mr-2" />
                  )}
                  Save Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Secure Vault Tab */}
        <TabsContent value="vault">
          <TenantSecureLoginsFolder
            tenantId={user?.organization_id}
            tenantName={businessProfile?.company_name || businessProfile?.name}
            getAuthHeaders={getAuthHeaders}
            canAccess={user?.role === 'admin' || user?.role === 'super_admin'}
          />
        </TabsContent>

        {/* SMTP Settings Tab */}
        <TabsContent value="smtp">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                <Mail size={20} className="text-slate-600" />
                Email Settings
              </CardTitle>
              <p className="text-sm text-slate-500">Configure how emails are sent to your clients</p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Use Custom SMTP Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Use Custom SMTP</p>
                  <p className="text-sm text-slate-500">
                    {useCustomSmtp 
                      ? 'Using your own email server for sending emails' 
                      : 'Using platform email service (emails will be sent on your behalf)'}
                  </p>
                </div>
                <Switch
                  checked={useCustomSmtp}
                  onCheckedChange={(checked) => {
                    if (!checked && smtpLocked) {
                      // Switching back to platform email
                      handleDisableCustomSmtp();
                    } else {
                      setUseCustomSmtp(checked);
                      if (checked) {
                        setSmtpSettings({ ...smtpSettings, is_active: true });
                      }
                    }
                  }}
                  disabled={saving}
                />
              </div>

              {/* Platform Email Info (when not using custom SMTP) */}
              {!useCustomSmtp && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-blue-600 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-blue-900">Platform Email Service Active</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Emails will be sent using the platform's email service with your business name as the sender. 
                        This is the recommended option if you don't have your own email server.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom SMTP Form (when using custom SMTP) */}
              {useCustomSmtp && (
                <>
                  {/* Locked State Banner */}
                  {smtpLocked && (
                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Lock className="text-green-600" size={20} />
                        <div>
                          <p className="font-medium text-green-900">SMTP Settings Configured</p>
                          <p className="text-sm text-green-700">Your custom email settings are active and locked.</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setSmtpLocked(false)}
                        className="border-green-300 text-green-700 hover:bg-green-100"
                      >
                        <Pencil size={14} className="mr-2" />
                        Edit Settings
                      </Button>
                    </div>
                  )}

                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${smtpLocked ? 'opacity-60 pointer-events-none' : ''}`}>
                    <div className="space-y-2">
                      <Label>SMTP Host</Label>
                      <Input
                        placeholder="smtp.example.com"
                        value={smtpSettings.host}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                        disabled={smtpLocked}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Port</Label>
                      <Input
                        type="number"
                        placeholder="587"
                        value={smtpSettings.port}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value) || 587 })}
                        disabled={smtpLocked}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        placeholder="your-email@example.com"
                        value={smtpSettings.username}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                        disabled={smtpLocked}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <div className="relative">
                        <Input
                          type={showSmtpPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={smtpSettings.password}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                          disabled={smtpLocked}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          disabled={smtpLocked}
                        >
                          {showSmtpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>From Email</Label>
                      <Input
                        placeholder="noreply@yourbusiness.com"
                        value={smtpSettings.from_email}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                        disabled={smtpLocked}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>From Name</Label>
                      <Input
                        placeholder="Your Business Name"
                        value={smtpSettings.from_name}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                        disabled={smtpLocked}
                      />
                    </div>
                  </div>

                  <div className={`flex items-center gap-2 ${smtpLocked ? 'opacity-60 pointer-events-none' : ''}`}>
                    <Switch
                      checked={smtpSettings.use_tls}
                      onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, use_tls: checked })}
                      disabled={smtpLocked}
                    />
                    <Label>Use TLS Encryption</Label>
                  </div>

                  {!smtpLocked && (
                    <div className="flex justify-end pt-4 border-t border-slate-100">
                      <Button onClick={handleSaveSmtp} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                        {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                        Save & Lock SMTP Settings
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Storage Settings Tab */}
        <TabsContent value="storage">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    <img src="https://www.idrive.com/images/idrive-e2-logo.svg" alt="iDrive e2" className="h-6" />
                    Cloud Storage Settings
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">Configure your own iDrive e2 cloud storage for files and media</p>
                </div>
                <a 
                  href="https://console.idrivee2.com/partner/a2gdesigns" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <ExternalLink size={16} />
                  Get Your Own Bucket
                </a>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Enable Custom Storage</p>
                  <p className="text-sm text-slate-500">Use your own S3-compatible storage bucket</p>
                </div>
                <Switch
                  checked={storageSettings.is_active}
                  onCheckedChange={(checked) => setStorageSettings({ ...storageSettings, is_active: checked })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Endpoint URL</Label>
                  <Input
                    placeholder="s3.us-west-1.wasabisys.com"
                    value={storageSettings.endpoint}
                    onChange={(e) => setStorageSettings({ ...storageSettings, endpoint: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Region</Label>
                  <Input
                    placeholder="us-west-1"
                    value={storageSettings.region}
                    onChange={(e) => setStorageSettings({ ...storageSettings, region: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bucket Name</Label>
                  <Input
                    placeholder="my-bucket"
                    value={storageSettings.bucket_name}
                    onChange={(e) => setStorageSettings({ ...storageSettings, bucket_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Access Key ID</Label>
                  <Input
                    placeholder="AKIAIOSFODNN7EXAMPLE"
                    value={storageSettings.access_key_id}
                    onChange={(e) => setStorageSettings({ ...storageSettings, access_key_id: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Secret Access Key</Label>
                  <div className="relative">
                    <Input
                      type={showStorageKey ? 'text' : 'password'}
                      placeholder="••••••••••••••••••••••••••••••••"
                      value={storageSettings.secret_access_key}
                      onChange={(e) => setStorageSettings({ ...storageSettings, secret_access_key: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowStorageKey(!showStorageKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showStorageKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-100">
                <Button onClick={handleSaveStorage} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                  {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                  Save Storage Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TenantSettings;
